import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Trash2, ChevronLeft, ChevronRight, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore, useAppStore } from '../store';
import { T } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? ((window.location.hostname === 'devapp.soviewfx.fr' || window.location.hostname === 'devadmin.soviewfx.fr') ? 'https://devhub.soviewfx.fr' : ((window.location.hostname === 'app.soviewfx.fr' || window.location.hostname === 'admin.soviewfx.fr') ? 'https://hub.soviewfx.fr' : '')) : '');

export default function AIHistoryPanel({ onClose, onSelectImage, projectId = null }) {
  const { token } = useAuthStore();
  const { lang } = useAppStore();
  const t = T[lang] || T.fr;
  
  const [generations, setGenerations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [deleting, setDeleting] = useState(null);
  
  useEffect(() => {
    fetchHistory();
  }, [projectId]);
  
  const fetchHistory = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const url = projectId 
        ? `${API_URL}/api/ai/history/project/${projectId}`
        : `${API_URL}/api/ai/history?limit=30`;
        
      const res = await fetch(url, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setGenerations(data);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async (generationId) => {
    if (!token) return;
    
    setDeleting(generationId);
    try {
      const res = await fetch(`${API_URL}/api/ai/history/${generationId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setGenerations(prev => prev.filter(g => g.generation_id !== generationId));
        toast.success('Image supprimée');
        if (selectedIndex !== null) {
          setSelectedIndex(null);
        }
      }
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };
  
  const handleSelect = (generation, index) => {
    setSelectedIndex(index);
  };
  
  const handleUseImage = () => {
    if (selectedIndex !== null && generations[selectedIndex]) {
      onSelectImage(generations[selectedIndex].image_url);
      onClose();
    }
  };
  
  const navigateImage = (direction) => {
    if (selectedIndex === null) return;
    const newIndex = selectedIndex + direction;
    if (newIndex >= 0 && newIndex < generations.length) {
      setSelectedIndex(newIndex);
    }
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const objectLabels = {
    store: 'Store banne',
    pergola: 'Pergola'
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="glass-light w-full max-w-4xl max-h-[85vh] rounded-2xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-secondary to-primary flex items-center justify-center">
              <Clock size={20} className="text-white" />
            </div>
            <div>
              <h2 className="font-display font-bold text-lg">{t.ai_history || 'Historique IA'}</h2>
              <p className="text-xs text-muted-foreground">
                {generations.length} {t.generations || 'génération(s)'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            data-testid="close-history-btn"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Thumbnails Grid */}
          <div className="w-1/2 border-r border-white/10 overflow-y-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="animate-spin text-secondary" size={32} />
              </div>
            ) : generations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Sparkles size={48} className="text-muted-foreground mb-4 opacity-50" />
                <p className="text-muted-foreground">{t.no_history || 'Aucune génération'}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  {t.generate_first || 'Générez votre première image IA'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {generations.map((gen, index) => (
                  <motion.div
                    key={gen.generation_id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className={`relative group cursor-pointer rounded-xl overflow-hidden aspect-square border-2 transition-all ${
                      selectedIndex === index 
                        ? 'border-secondary shadow-lg shadow-secondary/30' 
                        : 'border-transparent hover:border-white/30'
                    }`}
                    onClick={() => handleSelect(gen, index)}
                    data-testid={`history-item-${index}`}
                  >
                    <img
                      src={gen.image_url}
                      alt={`Generation ${index + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-2 left-2 right-2">
                        <p className="text-xs font-bold truncate">
                          {objectLabels[gen.object_type] || gen.object_type}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {formatDate(gen.created_at)}
                        </p>
                      </div>
                    </div>
                    
                    {/* Delete button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(gen.generation_id);
                      }}
                      disabled={deleting === gen.generation_id}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 hover:bg-red-500/80 transition-all"
                      data-testid={`delete-gen-${index}`}
                    >
                      {deleting === gen.generation_id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Trash2 size={14} />
                      )}
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
          
          {/* Preview */}
          <div className="w-1/2 flex flex-col">
            {selectedIndex !== null && generations[selectedIndex] ? (
              <>
                {/* Preview Image */}
                <div className="flex-1 relative p-4">
                  <div className="relative h-full rounded-xl overflow-hidden">
                    <img
                      src={generations[selectedIndex].image_url}
                      alt="Preview"
                      className="w-full h-full object-contain bg-black/30"
                    />
                    
                    {/* Navigation */}
                    {generations.length > 1 && (
                      <>
                        <button
                          onClick={() => navigateImage(-1)}
                          disabled={selectedIndex === 0}
                          className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/70 disabled:opacity-30 transition-all"
                        >
                          <ChevronLeft size={20} />
                        </button>
                        <button
                          onClick={() => navigateImage(1)}
                          disabled={selectedIndex === generations.length - 1}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/70 disabled:opacity-30 transition-all"
                        >
                          <ChevronRight size={20} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Info */}
                <div className="p-4 border-t border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-bold">
                        {objectLabels[generations[selectedIndex].object_type]}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(generations[selectedIndex].created_at)}
                      </p>
                    </div>
                    <span className="px-2 py-1 rounded-full bg-secondary/20 text-secondary text-xs font-bold">
                      {generations[selectedIndex].provider}
                    </span>
                  </div>
                  
                  {/* Config summary */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {generations[selectedIndex].config?.width && (
                      <span className="px-2 py-0.5 rounded bg-white/10 text-xs">
                        L: {generations[selectedIndex].config.width}cm
                      </span>
                    )}
                    {generations[selectedIndex].config?.height && (
                      <span className="px-2 py-0.5 rounded bg-white/10 text-xs">
                        H: {generations[selectedIndex].config.height}cm
                      </span>
                    )}
                    {generations[selectedIndex].config?.ral_color && (
                      <span className="px-2 py-0.5 rounded bg-white/10 text-xs">
                        RAL {generations[selectedIndex].config.ral_color}
                      </span>
                    )}
                  </div>
                  
                  <button
                    onClick={handleUseImage}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-secondary to-primary font-bold text-white shadow-lg shadow-secondary/30 hover:shadow-secondary/50 transition-all"
                    data-testid="use-image-btn"
                  >
                    {t.use_image || 'Utiliser cette image'}
                  </button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-center p-8">
                <div>
                  <Sparkles size={48} className="mx-auto text-muted-foreground opacity-30 mb-4" />
                  <p className="text-muted-foreground">
                    {t.select_image || 'Sélectionnez une image'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
